'use client';

/**
 * DataRoomPanel Component
 *
 * Main container for the data room view. Renders checklist progress,
 * loan documents section, source groups, and empty state when applicable.
 */

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useI18n } from '../../hooks/useI18n';
import { DataRoomChecklistOverlay, type DataRoomChecklistOverlayProps } from './DataRoomChecklistOverlay';
import { DataRoomDocumentRow, type DataRoomDocumentRowProps, type VerificationStatus } from './DataRoomDocumentRow';
import { DataRoomEmptyState } from './DataRoomEmptyState';
import { DataRoomSourceGroup } from './DataRoomSourceGroup';
import { type DocumentScope } from './DocumentScopeBadge';

// ============================================================================
// Types
// ============================================================================

export interface DataRoomPanelSourceGroup {
  /** Source entity type (e.g. borrower, entity) */
  sourceEntityType: string;
  /** Source entity ID */
  sourceEntityId: string;
  /** Source name */
  sourceName: string;
  /** Source label */
  sourceLabel: string;
  /** Documents in this group */
  documents: Omit<
    DataRoomDocumentRowProps,
    'onView' | 'onUnlink' | 'showUnlink' | 'onVerify' | 'onReject' | 'onRequestReupload' | 'showVerificationControls'
  >[];
  /** Whether the group is expanded */
  isExpanded: boolean;
  /** Toggle expansion callback */
  onToggle: () => void;
}

/** Required document for the merged checklist view (Issue #1145) */
export interface DataRoomRequiredDocument {
  checklistItemId: string;
  documentType: string;
  displayName: string;
  description?: string | null;
  category?: string | null;
  isRequired: boolean;
  status: 'missing' | 'pending' | 'verified' | 'rejected' | 'needs_reupload' | 'expired';
  /**
   * Issue #1631 M4 — provenance pin to a WarehouseLineLoanTypeProfile
   * version when this requirement was sourced from that profile's
   * required-docs list (vs. per-loan-product or universal defaults).
   * Drives the "required by [fund]" badge on the document row.
   */
  loanTypeProfileVersionId?: string | null;
  /**
   * Issue #1631 M4 — pre-resolved human label for the fund-attribution
   * badge ("Goldman · Line A · DSCR" or, when full fetch is too costly,
   * a generic "warehouse line" label). Optional because the source of
   * truth (the profile-version row) isn't always fetched at the page
   * level.
   */
  fundAttributionLabel?: string | null;
  attachment?: {
    id: string;
    name?: string;
    documentType?: string;
    category?: string;
    scope?: DocumentScope;
    verificationStatus?: VerificationStatus;
    rejectionReason?: string;
  } | null;
}

export interface DataRoomPanelProps {
  /** Documents directly associated with the loan */
  loanDocuments: Omit<
    DataRoomDocumentRowProps,
    'onView' | 'onUnlink' | 'showUnlink' | 'onVerify' | 'onReject' | 'onRequestReupload' | 'showVerificationControls'
  >[];
  /** Source groups (borrowers, entities, etc.) */
  sources: DataRoomPanelSourceGroup[];
  /** Checklist progress data (optional) */
  checklistProgress?: DataRoomChecklistOverlayProps;
  /** Required documents from checklist — unfulfilled show as placeholders (Issue #1145) */
  requiredDocuments?: DataRoomRequiredDocument[];
  /** Upload callback for placeholder requirement rows (Issue #1145) */
  onUploadForRequirement?: (checklistItemId: string, documentType: string) => void;
  /** Callback when viewing a document */
  onView?: (documentId: string) => void;
  /** Callback when unlinking a document */
  onUnlink?: (documentId: string) => void;
  /** Callback to open browse panel */
  onBrowse: () => void;
  /** Callback to open upload dialog */
  onUpload: () => void;
  /** Callback when verify button is clicked */
  onVerify?: (documentId: string) => void;
  /** Callback when reject button is clicked */
  onReject?: (documentId: string) => void;
  /** Callback when request reupload button is clicked */
  onRequestReupload?: (documentId: string) => void;
  /** Whether to show verification action controls */
  showVerificationControls?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const DataRoomPanel = ({
  loanDocuments,
  sources,
  checklistProgress,
  requiredDocuments,
  onUploadForRequirement,
  onView,
  onUnlink,
  onBrowse,
  onUpload,
  onVerify,
  onReject,
  onRequestReupload,
  showVerificationControls,
}: DataRoomPanelProps): React.ReactElement => {
  const { t } = useI18n();

  const totalDocuments = loanDocuments.length + sources.reduce((sum, s) => sum + s.documents.length, 0);
  const hasRequiredDocs = requiredDocuments && requiredDocuments.length > 0;

  // Build a lookup: attachment ID → fulfilled requirement (for merging badges onto doc rows)
  const fulfilledByAttachmentId = new Map<string, DataRoomRequiredDocument>();
  for (const req of requiredDocuments ?? []) {
    if (req.status !== 'missing' && req.attachment?.id) {
      fulfilledByAttachmentId.set(req.attachment.id, req);
    }
  }

  // Unfulfilled requirements become placeholder rows in the document list
  const unfulfilledDocs = (requiredDocuments ?? []).filter((d) => d.status === 'missing');

  // Fulfilled requirements that have NO matching loan document row
  // (attachment exists but isn't in loanDocuments — render as standalone rows)
  const loanDocIds = new Set(loanDocuments.map((d) => d.documentId).filter(Boolean));
  const orphanFulfilled = (requiredDocuments ?? []).filter(
    (d) => d.status !== 'missing' && d.attachment?.id && !loanDocIds.has(d.attachment.id),
  );

  const isEmpty = totalDocuments === 0 && !hasRequiredDocs;
  const hasLoanSection = loanDocuments.length > 0 || orphanFulfilled.length > 0 || unfulfilledDocs.length > 0;

  return (
    <Stack spacing={2}>
      {/* Checklist progress */}
      {checklistProgress && <DataRoomChecklistOverlay {...checklistProgress} />}
      {/* Empty state */}
      {isEmpty && <DataRoomEmptyState onBrowse={onBrowse} onUpload={onUpload} />}
      {/* Loan documents — merged with checklist requirements (Issue #1145) */}
      {hasLoanSection && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.secondary',
              px: 1.5,
              pb: 1,
            }}
          >
            {t('dataRoom.loanDocuments')}
          </Typography>

          {/* Actual loan documents — with requirement badge when they fulfill a checklist item */}
          {loanDocuments.map((doc) => {
            const matchedReq = doc.documentId ? fulfilledByAttachmentId.get(doc.documentId) : undefined;
            return (
              <DataRoomDocumentRow
                key={doc.documentId}
                {...doc}
                requirementLabel={
                  matchedReq ? (matchedReq.isRequired ? t('dataRoom.required') : t('dataRoom.optional')) : undefined
                }
                isRequired={matchedReq?.isRequired ?? doc.isRequired}
                fundAttributionLabel={matchedReq?.fundAttributionLabel ?? null}
                onView={onView}
                onUnlink={onUnlink}
                onVerify={onVerify}
                onReject={onReject}
                onRequestReupload={onRequestReupload}
                showVerificationControls={showVerificationControls}
              />
            );
          })}

          {/* Fulfilled requirements whose attachment isn't in loanDocuments */}
          {orphanFulfilled.map((doc) => (
            <DataRoomDocumentRow
              key={doc.checklistItemId}
              documentId={doc.attachment?.id}
              name={doc.attachment?.name || doc.displayName}
              documentType={doc.displayName}
              category={doc.category ?? undefined}
              scope={(doc.attachment?.scope as DocumentScope) ?? 'loan'}
              verificationStatus={doc.attachment?.verificationStatus as VerificationStatus | undefined}
              rejectionReason={doc.attachment?.rejectionReason ?? undefined}
              requirementLabel={doc.isRequired ? t('dataRoom.required') : t('dataRoom.optional')}
              isRequired={doc.isRequired}
              fundAttributionLabel={doc.fundAttributionLabel ?? null}
              onView={onView}
              onUnlink={onUnlink}
              onVerify={onVerify}
              onReject={onReject}
              onRequestReupload={onRequestReupload}
              showVerificationControls={showVerificationControls}
            />
          ))}

          {/* Unfulfilled requirements — placeholder rows inline with documents */}
          {unfulfilledDocs.map((doc) => (
            <DataRoomDocumentRow
              key={doc.checklistItemId}
              isPlaceholder
              checklistItemId={doc.checklistItemId}
              name={doc.displayName}
              documentType={doc.documentType}
              category={doc.category ?? undefined}
              scope="loan"
              isRequired={doc.isRequired}
              fundAttributionLabel={doc.fundAttributionLabel ?? null}
              onUploadForRequirement={onUploadForRequirement}
            />
          ))}

          {sources.length > 0 && <Divider sx={{ mt: 1 }} />}
        </Box>
      )}
      {/* Source groups */}
      {sources.map((source) => (
        <DataRoomSourceGroup
          key={`${source.sourceEntityType}:${source.sourceEntityId}`}
          sourceName={source.sourceName}
          sourceLabel={source.sourceLabel}
          documents={source.documents}
          isExpanded={source.isExpanded}
          onToggle={source.onToggle}
          onView={onView}
          onUnlink={onUnlink}
          onVerify={onVerify}
          onReject={onReject}
          onRequestReupload={onRequestReupload}
          showVerificationControls={showVerificationControls}
        />
      ))}
    </Stack>
  );
};

DataRoomPanel.displayName = 'DataRoomPanel';

export default DataRoomPanel;
